import { ChangeEvent, KeyboardEvent, useCallback, useState } from "react";
import styles from "./AddLinks.module.css";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import LinkOutIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/LinkOutIconPath";
import { useTranslation } from "@coworker/locales";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import TrashCanIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/TrashCanIconPath";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import ListItem from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/ListItem/ListItem";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import StyledModalFooter from "../StyledModalFooter/StyledModalFooter";
import { FixaInputField } from "@coworker/apprestructured/src/shared/wrappers/FixaInputField/FixaInputField";
import InputWrapper from "../InputWrapper/InputWrapper";
import useSaveGuardedFunction from "@coworker/apprestructured/src/save-guard/useSaveGuardedFunction";

const MAX_LINKS = 50;

function isValidLinkUrl(value: string): boolean {
  if (!/^https?:\/\//i.test(value)) {
    return false;
  }
  try {
    const url = new URL(value);
    return (
      (url.protocol === "http:" || url.protocol === "https:") &&
      url.hostname.length > 0
    );
  } catch {
    return false;
  }
}

type AddLinksProps = {
  disabled: boolean;
  links: string[];
  onLinksChanged: (links: string[]) => void;
};

function AddLinks({ disabled, links, onLinksChanged }: AddLinksProps) {
  const [showModal, setShowModal] = useState(false);
  const [modalLinks, setModalLinks] = useState<string[]>(links);
  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { t } = useTranslation();

  const closeAndResetModal = useCallback(() => {
    setShowModal(false);
    setInputValue("");
    setError(null);
    setModalLinks(links);
  }, [links]);

  const {
    saveGuardedFunction: handleCloseClick,
    enableSaveGuard,
    disableSaveGuard,
  } = useSaveGuardedFunction(closeAndResetModal);

  const hasAddedLinks = links.length > 0;

  const handleListViewItemClick = useCallback(() => {
    setModalLinks(links);
    setInputValue("");
    setError(null);
    setShowModal(true);
  }, [links]);

  const tryAddLink = useCallback(
    (trimmed: string): string[] | null => {
      if (!isValidLinkUrl(trimmed)) {
        setError(t("invalidLinkErrorString"));
        return null;
      }

      if (modalLinks.includes(trimmed)) {
        setError(t("linkAlreadyAddedErrorString"));
        return null;
      }

      if (modalLinks.length >= MAX_LINKS) {
        setError(t("tooManyLinksErrorString", { maxLinks: MAX_LINKS }));
        return null;
      }

      return [...modalLinks, trimmed];
    },
    [modalLinks, t]
  );

  const handleAddLink = useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed) {
      return;
    }

    const nextLinks = tryAddLink(trimmed);
    if (nextLinks === null) {
      return;
    }

    setModalLinks(nextLinks);
    setInputValue("");
    setError(null);
    enableSaveGuard();
  }, [inputValue, tryAddLink, enableSaveGuard]);

  const handleRemoveLink = useCallback(
    (link: string) => {
      setModalLinks((prev) => prev.filter((l) => l !== link));
      enableSaveGuard();
    },
    [enableSaveGuard]
  );

  const handleConfirmClick = useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed) {
      onLinksChanged(modalLinks);
      closeAndResetModal();
      disableSaveGuard();
      return;
    }

    const nextLinks = tryAddLink(trimmed);
    if (nextLinks === null) {
      return;
    }

    onLinksChanged(nextLinks);
    closeAndResetModal();
    disableSaveGuard();
  }, [
    inputValue,
    modalLinks,
    tryAddLink,
    onLinksChanged,
    closeAndResetModal,
    disableSaveGuard,
  ]);

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
      setError(null);
      enableSaveGuard();
    },
    [enableSaveGuard]
  );

  const handleInputKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleAddLink();
      }
    },
    [handleAddLink]
  );

  return (
    <>
      <ListItem
        title={t(hasAddedLinks ? "linksStringCapitalized" : "addLinksString")}
        description={
          hasAddedLinks
            ? links.length === 1
              ? `1 ${t("linkString")}`
              : `${links.length} ${t("linksString")}`
            : ""
        }
        icon={LinkOutIconPath}
        onClick={handleListViewItemClick}
        disabled={disabled}
        emphasised={hasAddedLinks}
        testId="add-links-list-item"
      />
      <FixaModal visible={showModal} handleCloseBtn={handleCloseClick}>
        <FixaSheets
          labelledById="task-management-add-links-header"
          header={
            <FixaModalHeader
              id="task-management-add-links-header"
              closeBtnClick={handleCloseClick}
              title={t("addLinksString")}
            />
          }
          footer={
            <StyledModalFooter>
              <FixaButton
                text={t("confirmString")}
                type="primary"
                onClick={handleConfirmClick}
                fluid
              />
            </StyledModalFooter>
          }
          fullSize
        >
          <FixaModalBody>
            <InputWrapper error={error}>
              <FixaInputField
                id="add-links-input"
                type="text"
                label={t("linkUrlString")}
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleInputKeyDown}
                disabled={disabled}
                data-testid="add-links-input"
              />
            </InputWrapper>
            {modalLinks.length > 0 && (
              <FixaListView id="addedLinksList">
                {modalLinks.map((link) => (
                  <FixaListViewItem
                    key={link}
                    className={styles["linkItem"]}
                    title={link}
                    controlIcon={null}
                    control="default"
                    quantityLabel={
                      <FixaButton
                        onClick={() => handleRemoveLink(link)}
                        iconOnly
                        ssrIcon={TrashCanIconPath}
                        size="small"
                        aria-label={`${t("removeLinkString")} ${link}`}
                      />
                    }
                  />
                ))}
              </FixaListView>
            )}
          </FixaModalBody>
        </FixaSheets>
      </FixaModal>
    </>
  );
}

export default AddLinks;
